import {DI_TOKENS} from '@/di/di.tokens';
import {Emitter} from '@pixi/particle-emitter';
import debounce from 'lodash.debounce';
import {Application, Container, Texture} from 'pixi.js';

import {injected} from 'brandi';

import {AssetsLoader} from '@/services/AssetsLoader/AssetsLoader';
import {MediaService} from '@/services/MediaService/MediaService';
import {GlobalWindow} from '@/types';
import {
  AnimationConfigType,
  TitleAnimationConfig,
  TitleAnimationScreenConfig,
  titleAnimationScreenConfigs,
} from '@/ui/components/TitleAnimation/TitleAnimationScreenConfigs';
import {titleAnimationConfigLeft, titleAnimationConfigRight} from '@/ui/components/TitleAnimation/titleAnimationConfig';

const RESIZE_DEBOUNCE = 1500;

enum AnimationSide {
  Left = 'left',
  Right = 'right',
}

export class TitleAnimation {
  private app!: Application;
  private particlesContainer!: Container;
  private particlesEmitterLeft!: Emitter;
  private particlesEmitterRight!: Emitter;
  private prevWidth = 0;

  constructor(
    private readonly globalWindow: GlobalWindow,
    private readonly assetsLoader: AssetsLoader,
  ) {
    this.onResize = debounce(this.onResize.bind(this), RESIZE_DEBOUNCE);
  }

  public async init(containerNode: HTMLDivElement): Promise<void> {
    const {width, height} = containerNode.getBoundingClientRect();

    this.app = new Application({
      width,
      height,
      antialias: true,
      resolution: this.getAppDPR(),
      backgroundAlpha: 0,
      autoDensity: true,
      resizeTo: containerNode,
    });

    containerNode.appendChild(this.app.view as HTMLCanvasElement);

    const style = this.app.view.style;

    if (style) {
      style.width = '100%';
      style.height = '100%';
    }

    // это нужно для локального использования браузерного расширения для пикси
    this.globalWindow.__PIXI_APP__ = this.app;

    this.createItems();
    this.globalWindow.addEventListener('resize', this.onResize);
  }

  public destroy(): void {
    this.app.view.parentNode?.removeChild(this.app.view);
    this.app.stage.removeAllListeners();
    this.app.stage.removeChildren();
    this.globalWindow.removeEventListener('resize', this.onResize);
    this.app.destroy();
  }

  public getStageDimensions(): {width: number; height: number} {
    if (!this.app) return {width: 0, height: 0};

    return {
      width: this.app.renderer.width,
      height: this.app.renderer.height,
    };
  }

  public getAppDPR(): number {
    return this.globalWindow.devicePixelRatio;
  }

  private getConfig = (side: AnimationSide): TitleAnimationConfig => {
    const {width, height} = this.getStageDimensions();
    const clientWidth = width / this.getAppDPR();
    const clientHeight = height / this.getAppDPR();

    let config: TitleAnimationScreenConfig = titleAnimationScreenConfigs[AnimationConfigType.MEDIUM];

    if (MediaService.isDesktop(clientWidth)) {
      config = titleAnimationScreenConfigs[AnimationConfigType.DESKTOP];
    }

    if (MediaService.isTablet(clientWidth)) {
      config = titleAnimationScreenConfigs[AnimationConfigType.TABLET];
    }

    if (MediaService.isMedium(clientWidth)) {
      config = titleAnimationScreenConfigs[AnimationConfigType.MEDIUM];
    }

    if (MediaService.isSmall(clientWidth)) {
      config = titleAnimationScreenConfigs[AnimationConfigType.SMALL];
    }

    return {
      scale: config.scale,
      rotation: config.rotation[side],
      frequency: config.frequency[side],
      position: {
        y: clientHeight,
        x: clientWidth / 2,
      },
    };
  };

  private createParticlesEmitterLeft(): Emitter {
    const {scale, rotation, frequency, position} = this.getConfig(AnimationSide.Left);

    return new Emitter(
      this.particlesContainer,
      titleAnimationConfigLeft(position, scale, rotation, frequency, this.getItemsTextures()),
    );
  }

  private createParticlesEmitterRight(): Emitter {
    const {scale, rotation, frequency, position} = this.getConfig(AnimationSide.Right);

    return new Emitter(
      this.particlesContainer,
      titleAnimationConfigRight(position, scale, rotation, frequency, this.getItemsTextures()),
    );
  }

  private getItemsTextures(): Texture[] {
    return [
      this.assetsLoader.itemsBundle.item1,
      this.assetsLoader.itemsBundle.item2,
      this.assetsLoader.itemsBundle.item3,
      this.assetsLoader.itemsBundle.item4,
      this.assetsLoader.itemsBundle.item5,
      this.assetsLoader.itemsBundle.item6,
      this.assetsLoader.itemsBundle.item7,
      this.assetsLoader.itemsBundle.item8,
      this.assetsLoader.itemsBundle.item9,
      this.assetsLoader.itemsBundle.item10,
      this.assetsLoader.itemsBundle.item11,
      this.assetsLoader.itemsBundle.item12,
      this.assetsLoader.itemsBundle.item13,
    ];
  }

  private createItems = (): void => {
    const rootContainer = new Container();
    this.app.stage.addChild(rootContainer);
    this.particlesContainer = new Container();
    this.particlesContainer.zIndex = 1;
    rootContainer.addChild(this.particlesContainer);

    this.particlesEmitterLeft = this.createParticlesEmitterLeft();
    this.particlesEmitterLeft.autoUpdate = true;

    this.particlesEmitterRight = this.createParticlesEmitterRight();
    this.particlesEmitterRight.autoUpdate = true;
  };

  private onResize = (): void => {
    const innerWidth = this.globalWindow.innerWidth;

    if (this.prevWidth !== innerWidth) {
      this.particlesEmitterLeft.destroy();
      this.particlesEmitterRight.destroy();
      this.app.stage.removeChildren(0);
      this.createItems();
    }
  };
}

injected(TitleAnimation, DI_TOKENS.globalWindow, DI_TOKENS.assetsLoader);
